import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SiteConfig } from '../../../config/site-config';
import { environment } from '../../../environments/environment';
import { AppState } from '../../app.state';
import { NotificationType } from '../../shared/models/notification-type';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { ErrorHandlerService } from '../../shared/services/error-handler/error-handler.service';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { User } from '../../shared/services/user/models/user.model';
import { UserService } from '../../shared/services/user/user.service';
import { ManageLocationsService } from '../services/manage-locations.service';
import { ManageLocationComponent } from './manage-location/manage-location.component';

@Component({
    selector: 'upsc-manage-locations',
    templateUrl: './manage-locations.component.html',
})
export class ManageLocationsComponent implements OnInit {

    public locationList: any[];
    public location: any;
    public defaultLocation: string;
    public locationId: string;
    public disableDefault: boolean;
    public isDefault: boolean;
    public isFRITUser: boolean;
    public isEUUser: boolean;
    public dtOptions: any = {};
    public dtTrigger: Subject<any> = new Subject<any>();
    public currentUserIsAdmin: boolean;
    public currentUserCountryCode: string;

    private currentUser: User;

    // @ViewChild('madeDefaultInfo') madeDefaultInfo: ElementRef;
    // @ViewChild('makeDefaultFailedInfo') makeDefaultFailedInfo: ElementRef;

    public constructor(private locationService: ManageLocationsService,
                       private notificationService: NotificationService,
                       private dialog: MatDialog,
                       private dialogService: DialogService,
                       private translateService: TranslateService,
                       private errorHandlerService: ErrorHandlerService,
                       private userService: UserService,
                       private readonly appState: AppState,
    ) {
        this.currentUser = this.appState.user$();
        this.currentUserIsAdmin = this.currentUser?.IsAdmin;
        this.defaultLocation = this.currentUser?.DefaultLocation;
        this.CheckIfEUUser();
        this.getLocations();
    }

    public ngOnInit() {
        this.dtOptions = SiteConfig.tableConfig;
    }

    public addLocation() {
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '600px',
            data: {
                editMode: false,
                isFRIT: this.isFRITUser,
            },
        };

        this.openManageLocation(dialogConfig);
    }

    public editLocation(contactId: string) {
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '600px',
            data: {
                contactId,
                editMode: true,
                disableDefault: this.disableDefault,
                isFRIT: this.isFRITUser,
            },
        };

        this.openManageLocation(dialogConfig);
    }

    public openManageLocation(dialogConfig: MatDialogConfig) {
        if (this.isEUUser && environment.feature.disableEditShipFromLocationEU) {
            const RestrictMessage = this.userService.GetRestrictShipFromAddressChangeMessage(this.currentUserCountryCode);
            this.dialogService.alert('', RestrictMessage);
            // this.dialogService.alert(
            //     '',
            //     this.translateService.instant('noShipFromEditAlert'),
            // );

            return;
        }

        const dialogRef: MatDialogRef<ManageLocationComponent> = this.dialog.open(ManageLocationComponent, dialogConfig);

        dialogRef.afterClosed().subscribe((result) => {
            this.handleManageLocationResult(result);
        });
    }

    public getLocations() {
        this.locationService.getLocations()
            .pipe(
                catchError((error) => {
                    this.dtTrigger.next(null);
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        this.translateService.instant('errorLoadingLocation'),
                        NotificationType.ERROR);

                    return of(null);
                }),
            )
            .subscribe(
                (locations) => {
                    this.locationList = locations || [];

                    // If only one location found, make it default and disable default checkbox in add/edit
                    this.disableDefault = this.locationList.length === 1;

                    this.defaultLocation = this.currentUser.DefaultLocation;
                    const defaultLocation = locations.find(location => location.ContactId === this.defaultLocation);
                    if (defaultLocation) {
                        defaultLocation.IsUserDefault = true;
                    }

                    this.dtTrigger.next(null);
                },
            );
    }

    public removeConfirmation(contactId: string) {
        this.dialogService.confirm(
            this.translateService.instant('confirmRemoveLocation'),
            this.translateService.instant('DeleteLocationWarning'))
            .subscribe(
                (isConfirmed) => {
                    if (!isConfirmed) {
                        return;
                    }

                    this.checkDefault(contactId);
                });
    }

    public makeDefaultLocation(contactId: string) {
        this.locationService.saveDefaultLocation(contactId).subscribe(
            (response) => {
                // Commented below code as response is null for success though
                //if (response.status === 202) {
                this.refreshUserPreferences();
                this.notificationService.notify('', this.translateService.instant('MadeDefaultInfo'), NotificationType.SUCCESS);
                // } else {
                //   this.notificationService.notify('', this.translateService.instant('MakeDefaultFailedInfo'), NotificationType.ERROR);
                // }
            },
            (error) => {
                this.notificationService.notify(
                    this.errorHandlerService.getHttpErrorMessage(error),
                    this.translateService.instant('errorMakingDefault'),
                    NotificationType.ERROR);
            });
    }

    private handleManageLocationResult(result) {
        if (result && result.result === 'success') {
            this.getLocations();
        }
    }

    private checkDefault(contactId: string) {
        this.locationService.checkDefaultLocation(contactId).subscribe(
            (response) => {
                if (response && response === true) { // [MV3-4035] changed according to response
                    this.dialogService.alert(
                        this.translateService.instant('defaultLocationWarning_Title'),
                        this.translateService.instant('DefaultLocationWarning'));
                } else {
                    this.deleteLocation(contactId);
                }
            },
            (error) => {
                this.notificationService.notify(
                    this.errorHandlerService.getHttpErrorMessage(error),
                    this.translateService.instant('errorCheckingDefaultlocation'),
                    NotificationType.ERROR);
            });
    }

    private deleteLocation(contactId: string) {
        this.locationService.deleteLocation(contactId).subscribe(
            (response) => {
                this.notificationService.notify('', this.translateService.instant('LocationDeletedInfo'), NotificationType.SUCCESS);
                this.getLocations(); // Refresh Grid to get the updated data list
            },
            (error) => {
                this.notificationService.notify(
                    this.errorHandlerService.getHttpErrorMessage(error),
                    this.translateService.instant('errorDeletingLocation'),
                    NotificationType.ERROR);
            });
    }

    private refreshUserPreferences() {
        this.userService.getUser()
            .subscribe(
                user => this.handleGetUserSuccess(user),
                err => this.handleGetUserFailure(err),
            );
    }

    private handleGetUserSuccess(user) {
        this.appState.user$.set(user);

        this.currentUser = user;
        this.currentUserIsAdmin = this.currentUser?.IsAdmin;
        this.defaultLocation = this.currentUser?.DefaultLocation;
        this.CheckIfEUUser();

        // const currentUser = {
        //     username: user.UserName,
        //     sessionid: user.MobileSessionID,
        //     defaultLocation: user.DefaultLocation,
        //     isAdmin: user.IsAdmin,
        // };
        //
        // this.currentUser = currentUser;
        // this.storage.set('currentuser', currentUser).subscribe(() => {});
    }

    private handleGetUserFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            this.translateService.instant('errorGettingUser'),
            NotificationType.ERROR);
    }

    private CheckIfEUUser() {
        this.isEUUser = this.userService.isEUUser(this.currentUser?.CountryCode);
        this.currentUserCountryCode = this.currentUser?.CountryCode;
        this.isFRITUser = ['IT', 'FR'].includes(this.currentUser?.CountryCode.toUpperCase());

        // this.sharedService.userSubject.subscribe(
        //     (user) => {
        //         if (!user) {
        //             return;
        //         }
        //        
        //         this.isEUUser = this.userService.isEUUser(user.CountryCode);
        //         this.currentUserCountryCode = user.CountryCode;
        //     },
        // );
    }
}
